/**
 * Merges a stored `ToolbarLayout` over the shipped default.
 *
 * Split out of `mainToolbarModel.ts` in task B14: threading a user layout
 * through the model pushed that file past the 200-line guideline, and the
 * merge is the half with its own test file already.
 *
 * Without this merge, a layout persisted by the customization UI would freeze
 * the toolbar at the set of items that existed the day the user customized:
 * every button shipped afterwards would be permanently invisible to them until
 * they hit Reset. The merge is what makes "unknown new ids default visible"
 * true, which is what F01-P3 requires.
 */
import {
  DEFAULT_TOOLBAR_LAYOUT,
  TOOLBAR_ANCHOR_FIRST_ID,
  TOOLBAR_ANCHOR_LAST_ID,
  UNHIDEABLE_TOOLBAR_ITEM_IDS,
  type ToolbarGating,
  type ToolbarItemId,
  type ToolbarLayout,
  type ToolbarMoreGroupId,
} from './toolbarItemTypes';

const UNHIDEABLE = new Set<ToolbarItemId>(UNHIDEABLE_TOOLBAR_ITEM_IDS);

/**
 * The anchor half of B14's policy, enforced HERE because this is the single
 * funnel every layout passes through on its way to a render — including the
 * raw layouts B15 and the tests hand in, which never meet the customization
 * normalizer.
 *
 * It has to run after the re-insertion pass, not before: re-inserting an id
 * that shipped later splices at its *default* index, which on a short or a
 * promoted-heavy stored row can land after More ▾ and quietly push the trigger
 * into the middle of the toolbar.
 */
function pinAnchors(primary: ToolbarItemId[]): ToolbarItemId[] {
  let out = primary;
  if (out.includes(TOOLBAR_ANCHOR_FIRST_ID) && out[0] !== TOOLBAR_ANCHOR_FIRST_ID) {
    out = [TOOLBAR_ANCHOR_FIRST_ID, ...out.filter((id) => id !== TOOLBAR_ANCHOR_FIRST_ID)];
  }
  if (out.includes(TOOLBAR_ANCHOR_LAST_ID) && out[out.length - 1] !== TOOLBAR_ANCHOR_LAST_ID) {
    out = [...out.filter((id) => id !== TOOLBAR_ANCHOR_LAST_ID), TOOLBAR_ANCHOR_LAST_ID];
  }
  return out;
}

/**
 * Each item's home group in the shipped default.
 *
 * The group gate has to travel WITH the descriptor rather than be applied to
 * whichever surface it lands on. `session-browser` and `instance-monitor`
 * carry no per-item `visible` predicate of their own — they are hidden purely
 * by the DBA group's gate — so once a layout can move an id between surfaces
 * (which is the whole point of B14/B15), gating by rendering-surface would
 * render them unconditionally with zero DBA-eligible connections.
 *
 * It is also what tells the customization UI where a demoted item lands: an id
 * with a home group goes back to it, one without (a default primary-row item)
 * has nowhere to fall back to and is hidden instead.
 */
export const TOOLBAR_HOME_GROUP: ReadonlyMap<ToolbarItemId, ToolbarMoreGroupId> = new Map(
  DEFAULT_TOOLBAR_LAYOUT.more.flatMap((group) =>
    group.items.map((id) => [id, group.group] as const),
  ),
);

/**
 * Is the group gate open for this item? Lives next to `TOOLBAR_HOME_GROUP`
 * because that map IS the gate's definition. Only the DBA group is gated;
 * primary-row items have no home group and are therefore always gate-open.
 *
 * Read by `buildMainToolbarModel` (to drop the item) and by the Settings
 * panel (to warn that ticking it may show nothing right now).
 */
export function isToolbarItemGateOpen(id: ToolbarItemId, gating: ToolbarGating): boolean {
  const home = TOOLBAR_HOME_GROUP.get(id);
  return home !== 'dba' || gating.hasDbaEligibleConnection;
}

/**
 * Re-inserts shipped ids the stored layout has never seen, each at the index it
 * occupies in the default order. `placed` is every id the stored layout
 * mentions ANYWHERE — including `hidden` and the other surface — so an item the
 * user moved or hid is not resurrected.
 */
function withUnseenItems(
  storedIds: ToolbarItemId[],
  defaultIds: ToolbarItemId[],
  placed: ReadonlySet<ToolbarItemId>,
): ToolbarItemId[] {
  const merged = [...storedIds];
  defaultIds.forEach((id, defaultIndex) => {
    if (placed.has(id)) return;
    merged.splice(Math.min(defaultIndex, merged.length), 0, id);
  });
  return merged;
}

/**
 * Passing `DEFAULT_TOOLBAR_LAYOUT` (the no-customization path) is a no-op —
 * every id is already placed and nothing is hidden.
 */
export function mergeToolbarLayout(stored: ToolbarLayout): ToolbarLayout {
  // Hiding More ▾ would strand every item inside it, and hiding Home would
  // remove the only way back to the start page: the anchors are unhideable
  // wherever the layout came from.
  const hidden = new Set((stored.hidden ?? []).filter((id) => !UNHIDEABLE.has(id)));
  const placed = new Set<ToolbarItemId>([
    ...stored.primary,
    ...stored.more.flatMap((group) => group.items),
    ...hidden,
  ]);

  // A stored layout can arrive hand-edited or imported from another machine,
  // so an id may repeat within a list or straddle both surfaces. Left alone it
  // would resolve to the same catalog entry twice and render two elements with
  // one testid. First occurrence wins, primary before the menu.
  const claimed = new Set<ToolbarItemId>();
  const claim = (ids: ToolbarItemId[]): ToolbarItemId[] =>
    ids.filter((id) => {
      if (claimed.has(id)) return false;
      claimed.add(id);
      return true;
    });

  const primaryStored = claim(stored.primary);
  // Same reasoning for a repeated GROUP: fold it into its first occurrence
  // rather than render the label and separator twice.
  const more: Array<{ group: ToolbarMoreGroupId; items: ToolbarItemId[] }> = [];
  for (const group of stored.more) {
    const items = claim(group.items);
    const existing = more.find((entry) => entry.group === group.group);
    if (existing) existing.items.push(...items);
    else more.push({ group: group.group, items });
  }
  DEFAULT_TOOLBAR_LAYOUT.more.forEach((defaultGroup, defaultIndex) => {
    const existing = more.find((group) => group.group === defaultGroup.group);
    if (existing) {
      existing.items = withUnseenItems(existing.items, defaultGroup.items, placed);
      return;
    }
    // A whole group shipped after the user customized (or they dropped every
    // item in it, in which case `placed` leaves nothing to re-add here).
    const items = defaultGroup.items.filter((id) => !placed.has(id));
    if (items.length > 0) more.splice(Math.min(defaultIndex, more.length), 0, { group: defaultGroup.group, items });
  });

  return {
    primary: pinAnchors(
      withUnseenItems(primaryStored, DEFAULT_TOOLBAR_LAYOUT.primary, placed).filter(
        (id) => !hidden.has(id),
      ),
    ),
    more: more.map((group) => ({
      group: group.group,
      items: group.items.filter((id) => !hidden.has(id)),
    })),
    hidden: stored.hidden,
  };
}
